import {
  getLocalLandingPageSeo,
  resolveLocalLandingFromPathname,
  resolveWorkerJoinLandingFromPathname,
  getWorkerJoinLandingPageSeo,
} from './localSeoLanding';
import { getGuideSeo } from '../data/guides';

/**
 * SEO config: per-path title and meta description for public pages.
 * Used so Google and users see distinct titles/descriptions per URL.
 */

export const DEFAULT_TITLE = 'Virtoly | Home Services Near You in Canada';
export const DEFAULT_DESCRIPTION =
  "Compare free quotes from local experts for cleaning, handyman, appliance repair, moving, and renovations. Book vetted home services near you across Canada on Virtoly.";

/**
 * Path prefixes that should not be indexed by search engines.
 * `/customer-` also matches `/customer-service` (the order flow).
 * Public category pages are excluded in {@link isPrivateSeoPath}.
 */
const PRIVATE_PATH_PREFIXES = [
  '/customer-',
  '/customer/',
  '/worker-',
  '/worker/',
  '/expert-',
  '/admin',
  '/manager',
  '/password',
  '/forget-pass',
  '/reset-pass',
  '/chat',
  '/notifications',
  '/favorite-experts',
  '/test-notification',
  '/addresses',
];

/** Exact account paths that sit outside the prefixes above. */
const PRIVATE_EXACT_PATHS = [
  '/payment-methods',
  '/available-projects',
  '/add-payment-info',
  '/payment-history',
];

/** Public paths that should not be indexed (legacy redirects and homepage modal deep links). */
const NOINDEX_PUBLIC_PATHS = [
  '/home',
  '/help/how-virtoly-works-expert',
  '/help/how-virtoly-works-customer',
];

export function isNoindexPublicPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  return NOINDEX_PUBLIC_PATHS.includes(normalized);
}

/** Marketing category pages. The bare `/customer-service` path is the private order flow. */
export function isPublicCategoryPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  return /^\/customer-service\/[^/]+$/.test(normalized);
}

export function isPrivateSeoPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (isPublicCategoryPath(normalized)) return false;
  if (normalized === '/password') return true;
  if (isNoindexPublicPath(normalized)) return true;
  if (PRIVATE_EXACT_PATHS.includes(normalized)) return true;
  return PRIVATE_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export interface PageSeo {
  title: string;
  description: string;
}

/** Turn URL slug into readable title (e.g. "cleaning-services" → "Cleaning Services") */
export function slugToTitle(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** SEO when /terms?subject= is set (canonical URL stays /terms with query). */
const TERMS_SUBJECT_SEO: Record<string, PageSeo> = {
  'acceptable-use': {
    title: 'Acceptable Use Policy | Virtoly Terms',
    description:
      'Virtoly acceptable use: prohibited conduct, bookings, scraping, payments, accounts, AI use, enforcement, and reporting.',
  },
  guarantee: {
    title: 'Virtoly Guarantee | Virtoly Terms',
    description:
      'Refunds up to order amount and optional Virtoly credit (Platform-only, not cashable) at Virtoly’s discretion. Not insurance; experts remain responsible for their work.',
  },
  cookies: {
    title: 'Cookie Policy | Virtoly Terms',
    description:
      'Virtoly Cookie Policy: cookies, pixels, GTM, analytics, Stripe, mobile SDKs, your choices, and opt-out resources.',
  },
  'fees-payments': {
    title: 'Fees, Payments & Cancellation | Virtoly Terms',
    description:
      'Supplemental terms: customer and expert charges, Stripe PSP, tax, fraud, cancellation and no-show schedules.',
  },
  'contact-details': {
    title: 'Contact Details | Virtoly Terms',
    description:
      'Virtoly legal contact details: website, email, Contact Us, copyright notices, and postal address requests.',
  },
};

/** Exact path → SEO (for main public pages) */
const PAGE_SEO: Record<string, PageSeo> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  '/find-experts-in-city': {
    title: 'Find Home Services Near You | Compare Local Experts | Virtoly',
    description:
      'Search by city and service to find cleaners, handymen, movers, and repair experts near you. Compare free quotes, read reviews, and book trusted local help on Virtoly.',
  },
  '/services': {
    title: 'Local Home Services — Cleaning, Handyman & More | Virtoly',
    description:
      'Explore home service categories on Virtoly — cleaning, handyman, moving, pet care, renovations, and more. Browse vetted local experts, compare quotes, and book the right professional for your project in Canada.',
  },
  '/service-areas': {
    title: 'Service Areas in Canada — Find Local Experts | Virtoly',
    description:
      'See where Virtoly operates across Canada. Find verified local experts in major cities nationwide, filter by location and service type, compare quotes, and book your expert online in minutes.',
  },
  '/guides': {
    title: 'Guides | Virtoly',
    description:
      'Practical guides for hiring local experts in Canada: cleaning, handyman, deck building, bathroom renovation, moving, mold inspection, and more. Tips, costs, and what to expect before you book.',
  },
  '/aboutUs': {
    title: 'About Us | Virtoly',
    description:
      'Learn about Virtoly — connecting customers with skilled service professionals. Trusted experts, transparent pricing.',
  },
  '/safety': {
    title: 'Safety | Virtoly',
    description:
      'How Virtoly keeps you safe: rated experts, badges, reviews, and secure payments.',
  },
  '/help-center': {
    title: 'Help Center | Virtoly',
    description:
      'FAQs and help for customers and experts. Orders, payments, accounts, and support.',
  },
  '/contactUs': {
    title: 'Contact Us | Virtoly',
    description:
      'Get in touch with Virtoly. Send us a message for support or inquiries.',
  },
  '/privacy': {
    title: 'Privacy Policy | Virtoly',
    description:
      'Virtoly privacy policy: collection, use, disclosure, retention, cookies, international transfers, and your rights (Canada, US, EEA/UK).',
  },
  '/terms': {
    title: 'Terms of Service | Virtoly',
    description:
      'Virtoly Terms of Service: user agreement, arbitration, payments, Acceptable Use, Virtoly Guarantee, Cookie Policy, fees and cancellation, and contact details.',
  },
  '/careers': {
    title: 'Careers | Virtoly',
    description:
      'Join Virtoly. Explore career opportunities and open positions.',
  },
  '/feedback': {
    title: 'Feedback | Virtoly',
    description:
      'Send feedback to Virtoly about booking a local expert, an order, or your experience on the site.',
  },
  '/request-callback': {
    title: 'Request a Callback | Virtoly',
    description:
      'Ask Virtoly to call you back about booking a local expert, an order, or your account.',
  },
  '/join': {
    title: 'Join Virtoly as an Expert | Virtoly',
    description:
      'Join Virtoly for free, set your service area, receive job requests, and get paid securely as a local expert in Canada.',
  },
};

/** Match /customer-service/:slug for category pages */
const CUSTOMER_SERVICE_PREFIX = '/customer-service/';

/**
 * Get title and description for a pathname. Returns null if path is not a public SEO page (e.g. dashboard).
 */
export function getPageSeo(pathname: string, search: string = ''): PageSeo | null {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (normalized === '/terms' && search) {
    const q = search.startsWith('?') ? search.slice(1) : search;
    const subject = new URLSearchParams(q).get('subject');
    if (subject && TERMS_SUBJECT_SEO[subject]) {
      return TERMS_SUBJECT_SEO[subject];
    }
  }
  const workerJoinResolved = resolveWorkerJoinLandingFromPathname(normalized);
  if (workerJoinResolved) {
    return getWorkerJoinLandingPageSeo(workerJoinResolved);
  }
  const localResolved = resolveLocalLandingFromPathname(normalized);
  if (localResolved) {
    return getLocalLandingPageSeo(localResolved);
  }
  if (PAGE_SEO[normalized]) {
    return PAGE_SEO[normalized];
  }
  if (normalized.startsWith(CUSTOMER_SERVICE_PREFIX)) {
    const slug = normalized.slice(CUSTOMER_SERVICE_PREFIX.length).split('/')[0];
    if (slug) {
      const categoryName = slugToTitle(slug);
      return {
        title: `${categoryName} — Book Local Experts | Virtoly`,
        description: `Find and book ${categoryName.toLowerCase()} from vetted local experts on Virtoly. Compare quotes, read reviews, and schedule online with secure payments — available in cities across Canada.`,
      };
    }
  }
  if (normalized.startsWith('/guides/')) {
    const guideSlug = normalized.slice('/guides/'.length).split('/')[0];
    if (guideSlug) {
      return getGuideSeo(guideSlug);
    }
  }
  return null;
}
